import { compose } from 'redux';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import MenuIcon from '@material-ui/icons/Menu';
import CloseIcon from '@material-ui/icons/Close';
import DownloadIcon from '@material-ui/icons/GetApp';
import UploadIcon from '@material-ui/icons/CloudUpload';
import withStyles from '@material-ui/styles/withStyles';
import { Box, Grid, Typography } from '@material-ui/core';
import { useCallback, useState, useMemo, memo } from 'react';
import ViewAgendaIcon from '@material-ui/icons/ViewAgenda';
import BarChartIcon from '@material-ui/icons/BarChart';

import {
  SpeedDial,
  SpeedDialIcon,
  SpeedDialAction,
  ToggleButton,
  ToggleButtonGroup,
} from '@material-ui/lab';

import { BasePage } from 'altus-ui-components';

import {
  getTrajectoryPreview,
  uploadWellboreTrajectory,
  getTrajectoryFromWellcomApi,
  downloadTrajectoryTemplateTxt,
  downloadTrajectoryTemplateCsv,
  downloadTrajectorySurveyTxt,
  downloadTrajectorySurveyCsv,
} from 'features/wells/overview/wellOverview.actions';

import { TrajectoryViewType } from 'app/components/WellboreTrajectory/WellboreTrajectoryViewToggle';

import { EMPTY_MAP, SystemPermission } from 'app/app.constants';
import { useHeader } from 'app/hooks/useHeader';
import useWellbore from 'features/wells/hooks/useWellbore';
import WellIntegrityGraphs from './components/WellIntegrityGraphs';
import { getSummarizedDataStateFromState } from 'app/app.selectors';
import HasSystemPermission from 'app/components/HasSystemPermission';
import { getWellboreTrajectoryFromState } from 'features/wells/wellbore.selectors';
import { WELLS_ACTIONS as WELLBORE_ACTIONS } from 'features/wells/wells.constants';
import { useSystemPermissions } from 'app/hooks/authorization/useSystemPermissions';
import { WELL_OVERVIEW_ACTIONS } from 'features/wells/overview/wellOverview.constants';
import WellDashboardProjectHistory from 'features/wells/projects/WellDashboardProjectHistory';
import WellboreTrajectoryDetails from 'app/components/WellboreTrajectory/WellboreTrajectoryDetails';
import WellboreTrajectoryPreviewModalContainer from 'features/wells/overview/components/WellboreTrajectoryPreviewModalContainer';
import WellboreContextTrajectory3DView from 'app/components/WellboreContextualization/WellboreContextTrajectory3DView';
import ActivityLogContainer from 'features/wells/overview/activityLog/ActivityLogContainer';
import {
  ACTIVITY_LOG_ACTIONS,
  ACTIVITY_VIEWS,
} from 'features/wells/overview/activityLog/helpers/activityLog.constants';
import { INTEGRITY_MANAGEMENT_ACTIONS } from 'features/settings/integrityManagement/helpers/integrityManagement.constants';

const WellOverviewContainer = ({
  classes,
  fieldId,
  dataState,
  breadcrumb,
  wellboreId,
  trajectory = EMPTY_MAP,
  dispatchUploadWellboreTrajectory,
  dispatchGetTrajectoryFromWellcomApi,
  dispatchGetWellboreTrajectoryPreview,
  dispatchDownloadTrajectoryTemplateTxt,
  dispatchDownloadTrajectorySurveyCsv,
  dispatchDownloadTrajectorySurveyTxt,
  dispatchDownloadTrajectoryTemplateCsv,
}) => {
  useHeader({
    subTitle: breadcrumb,
  });

  const { wellbore } = useWellbore(wellboreId);

  const [trajectoryFileToUpload, setTrajectoryFileToUpload] = useState();
  const [selectedView, setSelectedView] = useState(ACTIVITY_VIEWS.CHART);
  const toggleViewChange = useCallback((_event, value) => {
    if (!value) return;

    setSelectedView(value);
  }, []);

  const onDrop = useCallback(
    (files) => setTrajectoryFileToUpload(files[0]),
    [],
  );

  const {
    isDragActive,
    getRootProps,
    getInputProps,
    open: openTrajectoryFilePicker,
  } = useDropzone({
    onDrop,
    noClick: true,
    multiple: false,
    noKeyboard: true,
  });

  const [isActionsPopupOpen, setActionsPopupOpen] = useState(false);

  const [viewType] = useState(TrajectoryViewType.THREE_D);

  const actions = useMemo(
    () =>
      [
        {
          icon: <UploadIcon />,
          name: (
            <Typography variant="subtitle2" noWrap>
              Upload trajectory
            </Typography>
          ),
          onClick: openTrajectoryFilePicker,
        },
        {
          icon: <DownloadIcon />,
          name: (
            <Typography variant="subtitle2" noWrap>
              Template (.txt)
            </Typography>
          ),
          onClick: dispatchDownloadTrajectoryTemplateTxt,
        },
        {
          icon: <DownloadIcon />,
          name: (
            <Typography variant="subtitle2" noWrap>
              Template (.csv)
            </Typography>
          ),
          onClick: dispatchDownloadTrajectoryTemplateCsv,
        },

        {
          icon: <DownloadIcon />,
          name: (
            <Typography variant="subtitle2" noWrap>
              Survey (.txt)
            </Typography>
          ),
          hidden: !trajectory.get('trajectoryPoints')?.size,
          onClick: () => dispatchDownloadTrajectorySurveyTxt(wellboreId),
        },
        {
          icon: <DownloadIcon />,
          name: (
            <Typography variant="subtitle2" noWrap>
              Survey (.csv)
            </Typography>
          ),
          hidden: !trajectory.get('trajectoryPoints')?.size,
          onClick: () => dispatchDownloadTrajectorySurveyCsv(wellboreId),
        },
        {
          icon: <UploadIcon />,
          name: (
            <Typography variant="subtitle2" noWrap>
              Wellcom API
            </Typography>
          ),
          hidden: !(wellbore && wellbore.get('wellcomId') !== null),
          onClick: () => dispatchGetTrajectoryFromWellcomApi(wellboreId),
        },
      ].filter(({ hidden }) => !hidden),
    [
      wellbore,
      wellboreId,
      trajectory,
      openTrajectoryFilePicker,
      dispatchDownloadTrajectorySurveyTxt,
      dispatchDownloadTrajectorySurveyCsv,
      dispatchGetTrajectoryFromWellcomApi,
      dispatchDownloadTrajectoryTemplateTxt,
      dispatchDownloadTrajectoryTemplateCsv,
    ],
  );

  // dynamic width for displaying 3d trajectory
  const { userPermissions } = useSystemPermissions();
  const dynamicStyles = {
    maxWidth: userPermissions.includes(SystemPermission.WELL_ACCESS_ONLY)
      ? '50%'
      : '75%',
  };

  return (
    <BasePage
      dataState={dataState}
      LoadingOverlayProps={{
        timeout: 0,
      }}
      classes={{
        children: classes.basePageChildren,
        root: classNames({
          [classes.dragOver]: isDragActive,
        }),
      }}
    >
      <Grid
        xs
        item
        container
        {...getRootProps()}
        className={classes.topContainer}
      >
        <Grid
          item
          xs={3}
          component={Box}
          paddingLeft={2}
          className={classNames({
            [classes.wellboreDetails]: viewType === TrajectoryViewType.THREE_D,
          })}
        >
          <Typography variant="h5" noWrap>
            Survey
          </Typography>
          <Grid container className={classes.stickySection}>
            <Grid container item component={Box} marginBottom={2}>
              <WellboreTrajectoryDetails
                wellbore={wellbore}
                trajectory={trajectory}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          container
          xs
          className={classes.trajectoryArea3d}
          style={dynamicStyles}
        >
          <WellboreContextTrajectory3DView
            fieldId={fieldId}
            wellboreId={wellboreId}
          />
        </Grid>
        <HasSystemPermission permissions={SystemPermission.WELL_ACCESS_ONLY}>
          <Grid
            item
            xs={4}
            component={Box}
            paddingRight={2}
            className={classes.graphs}
          >
            <Typography variant="h5" noWrap className={classes.graphHeader}>
              Risk Matrix
            </Typography>
            <Grid container className={classes.stickySection}>
              <Grid container item component={Box} marginBottom={2}>
                <WellIntegrityGraphs wellboreId={wellboreId} />
              </Grid>
            </Grid>
          </Grid>
        </HasSystemPermission>
      </Grid>
      {userPermissions.includes(SystemPermission.WELL_ACCESS_ONLY) ? (
        <Grid xs item container className={classes.projectHistory}>
          <Grid item container>
            <Grid item xs={10} justifyContent="flex-start">
              <Typography variant="h5" noWrap className={classes.graphHeader}>
                Activity Log
              </Typography>
            </Grid>
            <Grid item xs={2} className={classes.buttonGroup}>
              <Grid item>
                <ToggleButtonGroup
                  exclusive
                  value={selectedView}
                  onChange={toggleViewChange}
                >
                  <ToggleButton value={ACTIVITY_VIEWS.CHART}>
                    <BarChartIcon />
                  </ToggleButton>
                  <ToggleButton value={ACTIVITY_VIEWS.LIST}>
                    <ViewAgendaIcon />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            </Grid>
          </Grid>
          <ActivityLogContainer
            wellboreId={wellboreId}
            fieldId={fieldId}
            selectedView={selectedView}
          />
        </Grid>
      ) : (
        <Grid xs item container className={classes.projectHistory}>
          <Typography variant="h5" noWrap className={classes.graphHeader}>
            Project History
          </Typography>
          <WellDashboardProjectHistory
            wellboreId={wellboreId}
            fieldId={fieldId}
          />
        </Grid>
      )}
      <SpeedDial
        icon={<SpeedDialIcon icon={<MenuIcon />} openIcon={<CloseIcon />} />}
        open={isActionsPopupOpen}
        className={classes.actionsButton}
        ariaLabel="Upload survey and download templates"
        onClose={(_, reason) =>
          reason !== 'mouseLeave' && setActionsPopupOpen(false)
        }
        onOpen={(_, reason) => reason !== 'focus' && setActionsPopupOpen(true)}
      >
        {actions.map(({ icon, name, onClick }, index) => (
          <SpeedDialAction
            key={index}
            icon={icon}
            tooltipOpen
            tooltipTitle={name}
            FabProps={{ size: 'medium' }}
            onClick={() => {
              setActionsPopupOpen(false);
              onClick();
            }}
          />
        ))}
      </SpeedDial>
      <WellboreTrajectoryPreviewModalContainer
        fieldId={fieldId}
        wellbore={wellbore}
        dataState={dataState}
        wellboreId={wellboreId}
        onClose={setTrajectoryFileToUpload}
        trajectoryFile={trajectoryFileToUpload}
        getTrajectoryPreview={dispatchGetWellboreTrajectoryPreview}
        uploadWellboreTrajectory={dispatchUploadWellboreTrajectory}
      />
      <input {...getInputProps()} />
    </BasePage>
  );
};

const styles = (theme) => ({
  basePageChildren: {
    flexDirection: 'column',
    paddingTop: theme.spacing(2.25),
    overflow: 'hidden',
  },
  actionsButton: {
    position: 'absolute',
    right: theme.spacing(4),
    bottom: theme.spacing(4),
  },
  createProjectButton: {
    position: 'absolute',
    left: theme.spacing(3),
  },
  wellboreDetails: {
    width: '100%',
    maxHeight: '100%',
    overflow: 'auto',
    right: theme.spacing(2),
    bottom: theme.spacing(2),
  },
  graphs: {
    width: '100%',
    maxHeight: '100%',
    overflow: 'auto',
    left: theme.spacing(2),
    bottom: theme.spacing(2),
  },
  dragOver: {
    opacity: '0.3',
    border: '5px gray dashed',
  },
  stickyHeader: {
    top: -theme.spacing(2.25),
  },
  stickySection: {
    top: 0,
    position: 'sticky',
    background: theme.palette.primary.darkgrey,
  },
  topContainer: {
    padding: '5px',
    maxHeight: '60%',
    position: 'relative',
    flexDirection: 'row',
    width: '100%',
    background: theme.palette.tabs.background.light,
  },
  trajectoryArea3d: {
    overflow: 'hidden',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    position: 'relative',
    // maxWidth moved to state to be dynamic
  },
  graphHeader: {
    paddingBottom: theme.spacing(1),
  },
  projectHistory: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(2),
    background: theme.palette.tabs.background.light,
    flexDirection: 'column',
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
});

export default compose(
  connect(
    (state, { wellboreId }) => {
      const trajectorySelector = getWellboreTrajectoryFromState(wellboreId);

      return {
        trajectory: trajectorySelector(state),
        dataState: getSummarizedDataStateFromState(
          state,
          WELLBORE_ACTIONS.REQUEST_WELLBORE_TRAJECTORY,
          WELL_OVERVIEW_ACTIONS.UPLOAD_WELLBORE_TRAJECTORY,
          INTEGRITY_MANAGEMENT_ACTIONS.GET_ALL_INTEGRITY_MANAGEMENT_INTEGRITIES_FOR_WELL,
          ACTIVITY_LOG_ACTIONS.GET_INTEGRITY_MANAGEMENT_USERS_BY_ORGANIZATION,
          ACTIVITY_LOG_ACTIONS.SEARCH_ACTIVITY_LOGS,
          ACTIVITY_LOG_ACTIONS.DELETE_ACTIVITY_LOG,
        ),
      };
    },
    {
      dispatchUploadWellboreTrajectory: uploadWellboreTrajectory,
      dispatchGetWellboreTrajectoryPreview: getTrajectoryPreview,
      dispatchDownloadTrajectoryTemplateCsv: downloadTrajectoryTemplateCsv,
      dispatchDownloadTrajectoryTemplateTxt: downloadTrajectoryTemplateTxt,
      dispatchGetTrajectoryFromWellcomApi: getTrajectoryFromWellcomApi,
      dispatchDownloadTrajectorySurveyCsv: downloadTrajectorySurveyCsv,
      dispatchDownloadTrajectorySurveyTxt: downloadTrajectorySurveyTxt,
    },
  ),
  memo,
  withStyles(styles),
)(WellOverviewContainer);
